import { PromptsConfig, AreaConfig } from '../types';
import { getMergedPrompts } from './customPromptsService';

// View mode types
export type ViewMode = 'role-based' | 'business-process';

// Config URLs for different views
const CONFIG_URLS: Record<ViewMode, string> = {
  'role-based': 'https://stpromptlib8312.blob.core.windows.net/config/prompts.json',
  'business-process': 'https://stpromptlib8312.blob.core.windows.net/config/prompts-business-process.json'
};

// Fallback to local config if blob storage fails
const LOCAL_CONFIG_URLS: Record<ViewMode, string> = {
  'role-based': '/config/prompts.json',
  'business-process': '/config/prompts-business-process.json'
};

const VIEW_MODE_STORAGE_KEY = 'promptLibrary_viewMode';

let cachedConfig: PromptsConfig | null = null;
let currentViewMode: ViewMode = 'role-based';

// Initialize view mode from localStorage
const initViewMode = (): ViewMode => {
  try {
    const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (stored === 'role-based' || stored === 'business-process') {
      return stored;
    }
  } catch (e) {
    console.warn('[ConfigService] Could not read from localStorage');
  }
  return 'role-based';
};

currentViewMode = initViewMode();

export const configService = {
  /**
   * Get the current view mode
   */
  getViewMode(): ViewMode {
    return currentViewMode;
  },

  /**
   * Set the view mode and reload config
   */
  async setViewMode(mode: ViewMode): Promise<PromptsConfig> {
    if (mode !== currentViewMode) {
      currentViewMode = mode;
      try {
        localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
      } catch (e) {
        console.warn('[ConfigService] Could not save to localStorage');
      }
      cachedConfig = null; // Clear cache to load new config
    }
    return this.loadConfig();
  },

  /**
   * Load the prompts configuration from the JSON file
   * Results are cached after first load
   * This method now loads merged prompts (standard + custom) from the backend
   */
  async loadConfig(): Promise<PromptsConfig> {
    if (cachedConfig) {
      console.log('[ConfigService] Returning cached config');
      return cachedConfig;
    }

    try {
      // Try to get merged prompts from backend (standard + custom)
      console.log('[ConfigService] Fetching merged prompts from backend');
      cachedConfig = await getMergedPrompts();
      console.log('[ConfigService] ✅ Loaded merged config with', cachedConfig?.areas?.length, 'areas');
      return cachedConfig;
    } catch (mergedError) {
      console.warn('[ConfigService] Failed to load merged prompts, falling back to standard prompts:', mergedError);

      // Fallback to standard prompts loading
      const configUrl = CONFIG_URLS[currentViewMode];
      const localConfigUrl = LOCAL_CONFIG_URLS[currentViewMode];

      // For local development, try local config first
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      if (isDevelopment) {
        console.log('[ConfigService] Development mode - trying local config first');
        try {
          const localResponse = await fetch(localConfigUrl, {
            cache: 'no-store',
            headers: {
              'Cache-Control': 'no-cache'
            }
          });
          if (localResponse.ok) {
            cachedConfig = await localResponse.json();
            console.log('[ConfigService] ✅ Loaded LOCAL config with', cachedConfig?.areas?.length, 'areas');
            return cachedConfig!;
          }
        } catch (localError) {
          console.warn('[ConfigService] Local config failed, will try blob storage:', localError);
        }
      }

      try {
        console.log('[ConfigService] Fetching config from:', configUrl);
        const response = await fetch(configUrl, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        console.log('[ConfigService] Response status:', response.status);
        if (!response.ok) {
          throw new Error(`Failed to load config: ${response.statusText}`);
        }

        cachedConfig = await response.json();
        console.log('[ConfigService] Loaded config with', cachedConfig?.areas?.length, 'areas');
        return cachedConfig!;
      } catch (error) {
        console.warn('[ConfigService] Blob storage failed, trying local config:', error);

        // Try local config as fallback
        try {
          const localResponse = await fetch(localConfigUrl, {
            cache: 'no-store'
          });
          if (localResponse.ok) {
            cachedConfig = await localResponse.json();
            console.log('[ConfigService] Loaded local config with', cachedConfig?.areas?.length, 'areas');
            return cachedConfig!;
          }
        } catch (localError) {
          console.error('[ConfigService] Local config also failed:', localError);
        }

        // Return default config if all loading fails
        return getDefaultConfig();
      }
    }
  },

  /**
   * Get all areas from the configuration
   */
  async getAreas(): Promise<AreaConfig[]> {
    const config = await this.loadConfig();
    return config.areas;
  },

  /**
   * Get a specific area by ID
   */
  async getArea(areaId: string): Promise<AreaConfig | undefined> {
    const config = await this.loadConfig();
    return config.areas.find(area => area.id === areaId);
  },

  /**
   * Get the default area
   */
  async getDefaultArea(): Promise<AreaConfig | undefined> {
    const config = await this.loadConfig();
    return config.areas.find(area => area.id === config.settings.defaultAreaId) 
      || config.areas[0];
  },

  /**
   * Clear the cached configuration (useful for reloading)
   */
  clearCache(): void {
    cachedConfig = null;
  },

  /**
   * Reload the configuration from the server
   */
  async reloadConfig(): Promise<PromptsConfig> {
    this.clearCache();
    return this.loadConfig();
  }
};

/**
 * Default configuration fallback
 */
function getDefaultConfig(): PromptsConfig {
  return {
    areas: [
      {
        id: 'general',
        name: 'General',
        description: 'General inquiries',
        icon: 'Chat',
        color: '#0078D4',
        prompts: [
          {
            id: 'gen-1',
            title: 'Help',
            text: 'What can you help me with?',
            category: 'General'
          }
        ]
      }
    ],
    settings: {
      defaultAreaId: 'general',
      showCategories: true,
      maxPromptsPerArea: 6
    }
  };
}

export default configService;
